"use client";

import { useState } from "react";

import type { CSSProperties, ReactNode } from "react";

import { useBodyScrollLock } from "@/lib/use-body-scroll-lock";

/**
 * Shared shell for every badge/mark's tap-to-learn-more modal. Adopts the
 * active profile theme: the accent bar and panel border read
 * `--paw-card-accent` (set to the theme's accent on the profile frame in
 * app/[handle]/page.tsx), replacing the old fixed red `paw-hazard` band.
 */
function BadgeModal({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  useBodyScrollLock(open);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      <div
        className="w-full max-w-sm border bg-(--paw-panel) p-6"
        style={{ borderColor: "color-mix(in srgb, var(--paw-card-accent, #2a2a2a) 42%, #2a2a2a)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="mb-4 h-1 w-full"
          style={{ background: "var(--paw-card-accent, var(--paw-signal))" }}
        />
        {children}
        <button
          type="button"
          onClick={onClose}
          className="paw-btn-ghost mt-5 w-full py-3 text-sm"
        >
          Close
        </button>
      </div>
    </div>
  );
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  kinkster:
    "A kinkster is someone who enjoys kink and BDSM activities as part of their identity and lifestyle.",
  leather:
    "A leatherperson identifies with the leather subculture — a tradition of community, craft, and values within the broader kink world.",
  pup: "A pup embraces the pup identity — playful, loyal, and part of a pack. Pups may have handlers or run with other pups.",
  handler:
    "A handler guides and cares for their pup(s), building trust and connection through training, mentorship, and companionship.",
  furry:
    "A furry engages with anthropomorphic animal characters and the furry community, often through art, roleplay, and social connection.",
};

/**
 * Clickable role badge that opens a modal explaining the role.
 * Ported from role-badge.blade.php — the stamp treatment with
 * x-data/x-teleport replaced by React state + portal.
 */
export function RoleBadge({
  role,
  stamp = false,
  accent,
}: {
  role: string;
  stamp?: boolean;
  accent?: string;
}) {
  const [open, setOpen] = useState(false);
  const label =
    { kinkster: "Kinkster", leather: "Leather", pup: "Pup", handler: "Handler", furry: "Furry" }[
      role
    ] ?? "Member";
  const description = ROLE_DESCRIPTIONS[role] ?? null;

  const style = accent
    ? ({ "--paw-badge-color": accent, "--paw-badge-wash": `color-mix(in srgb, ${accent} 12%, transparent)` } as CSSProperties)
    : undefined;

  return (
    <span className="inline-flex">
      <button
        type="button"
        onClick={() => description && setOpen(true)}
        style={style}
        className={stamp ? "paw-badge-stamp" : "paw-badge-box"}
      >
        {label}
      </button>

      <BadgeModal open={open && !!description} onClose={() => setOpen(false)}>
        <div className="flex items-center gap-2.5">
          <span className={stamp ? "paw-badge-stamp" : "paw-badge-box"} style={style}>
            {label}
          </span>
        </div>
        <p className="mt-3 text-sm leading-6 text-left normal-case" style={{ fontFamily: "var(--font-paw-sans)", textTransform: "none" }}>{description}</p>
      </BadgeModal>
    </span>
  );
}

/**
 * Generic clickable badge pill. Opens a modal explaining the badge.
 * Ported from badge-pill.blade.php — same x-data/x-teleport pattern.
 */
export function BadgePill({
  label,
  description,
  color = "var(--paw-steel-dim)",
  wash = "transparent",
  className = "paw-badge-box",
}: {
  label: string;
  description?: string | null;
  color?: string;
  wash?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const style = { "--paw-badge-color": color, "--paw-badge-wash": wash } as CSSProperties;

  return (
    <span className="inline-flex">
      <button
        type="button"
        onClick={() => description && setOpen(true)}
        style={style}
        className={className}
      >
        {label}
      </button>

      <BadgeModal open={open && !!description} onClose={() => setOpen(false)}>
        <div className="flex items-center gap-2.5">
          <span className={className} style={style}>{label}</span>
        </div>
        <p className="mt-3 text-sm leading-6 text-left normal-case" style={{ fontFamily: "var(--font-paw-sans)", textTransform: "none" }}>{description}</p>
      </BadgeModal>
    </span>
  );
}

/**
 * Verified tick — scalloped seal badge next to the display name.
 * Clickable, opens modal explaining verification.
 * Ported from verified-tick.blade.php.
 */
export function VerifiedSeal({ staff }: { staff: boolean }) {
  const [open, setOpen] = useState(false);
  const color = staff ? "#d9a520" : "#4a7ec2";
  const wash = staff ? "rgba(217,165,32,0.10)" : "rgba(74,126,194,0.10)";
  const description = staff
    ? "This is a Collr staff account. Verified badges confirm this account belongs to the person it claims to be."
    : "Verified badges confirm this account belongs to the person it claims to be.";

  return (
    <span className="inline-flex align-middle">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex"
        aria-label="Verified — tap to learn more"
      >
        <svg
          className="align-middle"
          style={{ color, width: "20px", height: "20px" }}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01zm.287 5.984-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7 8.793l2.646-2.647a.5.5 0 0 1 .708.708" />
        </svg>
      </button>

      <BadgeModal open={open} onClose={() => setOpen(false)}>
        <div className="flex items-center gap-2.5">
          <svg
            className="h-6 w-6"
            style={{ color }}
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01zm.287 5.984-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7 8.793l2.646-2.647a.5.5 0 0 1 .708.708" />
          </svg>
          <span className="paw-badge-box" style={{ "--paw-badge-color": color, "--paw-badge-wash": wash } as React.CSSProperties}>Verified</span>
        </div>
        <p className="mt-3 text-sm leading-6 text-left normal-case" style={{ fontFamily: "var(--font-paw-sans)", textTransform: "none" }}>{description}</p>
      </BadgeModal>
    </span>
  );
}

/**
 * Premium/Premium+ star badge next to the display name. Clickable — opens
 * a modal explaining the tier. Ported from premium-badge.blade.php.
 * Renders nothing for free/lapsed accounts.
 */
export function PremiumBadge({ tier }: { tier: string | null }) {
  const [open, setOpen] = useState(false);
  if (!tier) return null;

  const isPlus = tier === "premium_plus";
  const label = isPlus ? "Premium+" : "Premium";
  const color = isPlus ? "var(--paw-signal)" : "var(--paw-positive)";

  return (
    <span className="inline-flex align-middle">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex"
        aria-label={`${label} member — tap to learn more`}
      >
        <svg
          className="h-5 w-5 align-middle"
          style={{ color }}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 1a.75.75 0 0 1 .692.462l1.41 3.393 3.664.293a.75.75 0 0 1 .428 1.317l-2.791 2.39.853 3.575a.75.75 0 0 1-1.12.814L8 11.202l-3.135 1.892a.75.75 0 0 1-1.12-.814l.852-3.575-2.79-2.39a.75.75 0 0 1 .428-1.318l3.663-.293 1.41-3.393A.75.75 0 0 1 8 1Z" />
        </svg>
      </button>

      <BadgeModal open={open} onClose={() => setOpen(false)}>
        <div className="flex items-center gap-2.5">
          <svg
            className="h-[1.25em] w-[1.25em]"
            style={{ color }}
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 1a.75.75 0 0 1 .692.462l1.41 3.393 3.664.293a.75.75 0 0 1 .428 1.317l-2.791 2.39.853 3.575a.75.75 0 0 1-1.12.814L8 11.202l-3.135 1.892a.75.75 0 0 1-1.12-.814l.852-3.575-2.79-2.39a.75.75 0 0 1 .428-1.318l3.663-.293 1.41-3.393A.75.75 0 0 1 8 1Z" />
          </svg>
          <span className="paw-badge-box" style={{ "--paw-badge-color": color } as React.CSSProperties}>{label}</span>
        </div>
        <p className="mt-3 text-sm leading-6 text-left normal-case" style={{ fontFamily: "var(--font-paw-sans)", textTransform: "none" }}>
          This member subscribes to Collr {label}.
        </p>
      </BadgeModal>
    </span>
  );
}
